import { TypedDocumentNode, gql } from '@apollo/client';
import { isBefore, isFuture, isPast } from 'date-fns';
import { useMemo } from 'react';

import { CardRarity, Rarity, Sport } from '__generated__/globalTypes';
import { useQuery } from 'hooks/graphql/useQuery';
import { useCardsOnSale } from 'hooks/search/useCardsOnSale';
import {
  MonetaryAmountParams,
  getMonetaryAmountIndex,
  monetaryAmountFragment,
} from 'lib/monetaryAmount';

import {
  LowestSalesPerPlayerQuery,
  LowestSalesPerPlayerQueryVariables,
} from './__generated__/useLowestSalePerPlayer.graphql';

const LOWEST_SALES_PER_PLAYER_QUERY = gql`
  query LowestSalesPerPlayerQuery($slugs: [String!]!) {
    anyCards(slugs: $slugs) {
      slug
      pictureUrl
      anyPlayer {
        slug
      }
      liveSingleSaleOffer {
        id
        receiverSide {
          id
          amounts {
            ...MonetaryAmountFragment_monetaryAmount
          }
        }
      }
      latestPrimaryOffer {
        id
        endDate
        price {
          ...MonetaryAmountFragment_monetaryAmount
        }
      }
      latestEnglishAuction {
        id
        endDate
        currentPrice
        currency
      }
      user {
        slug
      }
    }
  }
  ${monetaryAmountFragment}
` as TypedDocumentNode<
  LowestSalesPerPlayerQuery,
  LowestSalesPerPlayerQueryVariables
>;

type Props = {
  slugs: string[];
  sport: Sport;
  skip?: boolean;
  season?: string | number;
  rarity?: CardRarity | Rarity | null;
  teamSlug?: string;
};

export type LowestPlayerCardSale = {
  pictureUrl?: string | null;
  amount?: MonetaryAmountParams;
  buyNowAmount?: MonetaryAmountParams;
  auctionCount?: number;
  auctionEndingSoonest?: {
    amount: MonetaryAmountParams;
    endDate: Date;
  };
};

type Card = LowestSalesPerPlayerQuery['anyCards'][0];

const isPrimaryOfferLive = (card: Card) => {
  const { latestPrimaryOffer } = card;
  const endDate = latestPrimaryOffer?.endDate;
  const price = latestPrimaryOffer?.price;
  const tokenOwner = card?.user;
  const ended = endDate && isPast(endDate);
  const isOnSale = !!latestPrimaryOffer && !tokenOwner && !ended;
  return isOnSale && price;
};

const isAuctionLive = (
  auction: Card['latestEnglishAuction']
): auction is NonNullable<Card['latestEnglishAuction']> => {
  const endDate = auction?.endDate;
  if (!endDate) return false;
  const auctionIsOnSale = isFuture(endDate);
  const ended = isPast(endDate);
  const price = auction?.currentPrice;
  return auctionIsOnSale && !ended && Boolean(price);
};

const getAuctionAmount = (auction: Card['latestEnglishAuction']) => ({
  referenceCurrency: auction!.currency,
  [getMonetaryAmountIndex(auction!.currency)]: auction!.currentPrice,
});

const OPTIONAL_FILTERS = [
  'sale.type:PrimaryOffer',
  'sale.type:SingleSaleOffer',
];

export function useLowestSalePerPlayer({
  slugs,
  sport,
  skip = false,
  season,
  rarity,
  teamSlug,
}: Props) {
  const { slugs: cardSlugs } = useCardsOnSale({
    index: 'Lowest Price',
    // boost the instant buy & single sale offers above the auctions
    optionalFilters: OPTIONAL_FILTERS,
    limit: 100,
    distinct: true,
    primaryOffer: true,
    withBundled: false,
    sport,
    season,
    rarities: rarity ? [rarity] : undefined,
    teamSlug,
    playerSlugs: slugs,
    skip,
  });

  const { data } = useQuery(LOWEST_SALES_PER_PLAYER_QUERY, {
    variables: { slugs: cardSlugs },
    skip: !cardSlugs?.length,
  });

  return useMemo(() => {
    const mappedTokensData = data?.anyCards.reduce<
      Record<string, LowestSalesPerPlayerQuery['anyCards'][number]>
    >((acc, card) => {
      return {
        ...acc,
        [card.slug]: card,
      };
    }, {});

    const saleByPlayer = cardSlugs.reduce<Record<string, LowestPlayerCardSale>>(
      (acc, slug) => {
        const card = mappedTokensData?.[slug];
        if (!card) return acc;
        const {
          anyPlayer,
          pictureUrl,
          liveSingleSaleOffer,
          latestEnglishAuction,
          latestPrimaryOffer,
        } = card;
        const { slug: playerSlug } = anyPlayer;
        if (!acc[playerSlug]) acc[playerSlug] = {};
        if (!acc[playerSlug]?.amount) {
          if (liveSingleSaleOffer) {
            const amount = liveSingleSaleOffer.receiverSide.amounts;
            acc[playerSlug].amount = amount;
            acc[playerSlug].buyNowAmount = amount;
            acc[playerSlug].pictureUrl = pictureUrl;
          } else if (isPrimaryOfferLive(card)) {
            const amount = latestPrimaryOffer!.price!;
            acc[playerSlug].pictureUrl = pictureUrl;
            acc[playerSlug].amount = amount;
            acc[playerSlug].buyNowAmount = amount;
          } else if (isAuctionLive(latestEnglishAuction)) {
            acc[playerSlug].amount = getAuctionAmount(latestEnglishAuction);
            acc[playerSlug].pictureUrl = pictureUrl;
          }
        }
        if (isAuctionLive(latestEnglishAuction)) {
          const prevAuction = acc[playerSlug].auctionEndingSoonest;
          const { endDate: prevEndDate } = prevAuction || {};
          const { endDate: currEndDate } = latestEnglishAuction;
          if (!prevEndDate || isBefore(currEndDate, prevEndDate)) {
            acc[playerSlug].auctionEndingSoonest = {
              endDate: currEndDate,
              amount: getAuctionAmount(latestEnglishAuction),
            };
          }
          acc[playerSlug].auctionCount =
            (acc[playerSlug].auctionCount ?? 0) + 1;
        }
        return acc;
      },
      {}
    );
    const missingPlayers = slugs.filter(
      slug => !saleByPlayer[slug]?.pictureUrl
    );
    return { saleByPlayer, missingPlayers };
  }, [cardSlugs, data?.anyCards, slugs]);
}
